<template>
  <div class="base-form-select">
    <BaseSelect
      :name="name"
      :label="label"
      :hasIcon="hasIcon"
      :disabled="disabled"
      :errors="errors"
      @click="toggleSelect()"
    >
      <template v-if="isLoading">
        <SkeleLoader v-if="isLoading" lines="1" contentIsFullWidth />
      </template>
      <template v-else>
        <component :is="loadIconPath" v-if="iconType" :iconColor="iconColor" />

        <DropdownSelect
          :isOpen="isOpen"
          :hasOneItemOnly="listHasOneItem"
          @onClickOutside="handleClickOutside"
        >
          <template #selectedItem>
            <div class="dropdown-select__selected-item">
              <span
                v-if="isMultiSelect"
                class="dropdown-select__multi-placeholder"
              >Select up to max {{ maxItems }} items from the list</span
              >
              <span
                v-else-if="selectedSingle"
                :id="`dropdownSelectedItemRef${name}`"
                @keydown.enter="toggleSelect(false)"
                @keydown.space="toggleSelect(false)"
                tabindex="0"
              >
                <span
                  v-if="Object.getOwnPropertyDescriptor(selectedSingle, 'name')"
                >{{ selectedSingle.name }}</span
                >
                <span v-else v-html="selectedSingle" />
              </span>
            </div>
          </template>
          <template #dropdownOptions>
            <SelectList
              :name="name"
              :items="selectList"
              :counterIndex="counterIndex"
              :isOpen="isOpen"
              :id="`formSelectList${name}`"
              @selectElement="selectEl"
              @selectElOnClick="selectElOnClick"
              @moveToNextElement="goToNextEl"
              @moveToPreviousElement="goToPreviousEl"
              @close="toggleSelect(false)"
              @click="toggleSelect()"
            >
              <template #selectItem="{ item }">
                <span
                  class="select-list__text"
                  v-if="Object.getOwnPropertyDescriptor(item, 'name')"
                >
                  {{ item.name }}

                  <pill v-if="item.isDisabled">Unsupported</pill>
                </span>

                <span class="select-list__text" v-else v-html="item" />
              </template>
            </SelectList>
          </template>
        </DropdownSelect>
      </template>
    </BaseSelect>

    <DropdownPillList
      v-if="isMultiSelect"
      @deselect-clicked="handleDeselectClick"
      :selectedItems="selectedMulti"
    />
  </div>
</template>

<script setup>
import { watch, computed, defineAsyncComponent } from 'vue';

import useSelectList from '@/composables/ui/useSelectList';

import BaseSelect from '@/components/ui/inputs/BaseSelect.vue';
import DropdownSelect from '@/components/ui/selects/DropdownSelect.vue';
import SelectList from '@/components/ui/selects/SelectList.vue';
import DropdownPillList from '@/components/ui/selects/DropdownPillList.vue';
import Pill from '@/components/ui/pills/Pill.vue';
import SkeleLoader from '@/components/ui/loaders/SkeleLoader.vue';

const props = defineProps({
  name: { type: String, default: 'default-select' },
  disabled: { type: Boolean, default: false },
  defaultItem: { type: [String, Object], default: '' },
  items: { type: Array, required: true },
  label: { type: String, default: 'Default label' },
  placeholder: { type: String, default: 'Default placeholder' },
  iconType: { type: String, required: false, default: '' },
  iconColor: { type: String, required: false, default: '' },
  id: { type: String, required: false, default: 'dropdownSelectedItemRef' },
  isMultiSelect: { type: Boolean, required: false, default: false },
  maxItems: { type: Number, required: false, default: 1 },
  allowSort: { type: Boolean, required: false, default: true },
  isLoading: { type: Boolean, default: false },
  errors: { type: String, default: null },
});

const { id } = props;

const emit = defineEmits(['dropdownValueUpdate']);
const hasIcon = !!props.iconType;

const {
  isOpen,
  counterIndex,
  selectList,
  goToNextEl,
  selectEl,
  deselectElement,
  selectElOnClick,
  goToPreviousEl,
  selectedSingle,
  selectedMulti,
  toggleSelect,
  updateCurrentList,
  listHasOneItem,
  handleClickOutside,
  toggleSort,
} = useSelectList({
  items: props.items,
  defaultItem: props.defaultItem,
  isMultiSelect: props.isMultiSelect,
  maxItems: props.maxItems,
});

const handleDeselectClick = (item, index) => {
  deselectElement(index);
};

const loadIconPath = computed(() => defineAsyncComponent(() => import(
      /* @vite-ignore */ `../../../components/icons/${props.iconType}.vue`
    )));

if (props.isMultiSelect) {
  watch(
    selectedMulti,
    () => {
      emit('dropdownValueUpdate', {
        [props.name]: selectedMulti.value,
      });
    },
    { deep: true },
  );
} else {
  watch(selectedSingle, () => {
    emit('dropdownValueUpdate', {
      [props.name]: selectedSingle.value,
    });
  });
}

watch(
  () => props.items,
  (newItems, prevItems) => {
    updateCurrentList(newItems, props.defaultItem);
  },
  { deep: true },
);

watch(
  () => props.allowSort,
  () => {
    toggleSort(props.allowSort);
  },
  { deep: true },
);

watch(
  () => props.defaultItem,
  () => {
    updateCurrentList(props.items, props.defaultItem);
  },
  { deep: true },
);
</script>

<style lang="scss">
.select-list {
  &__text {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  &__multi-placeholder {
    opacity: 0.6;
  }
}

.base-field {
  &.hasErrors {
    .dropdown-select {
      outline: 1px solid var(--error-color);
    }
  }
}
</style>
